import React, {
  createContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { toast } from 'react-toastify';
import { useQuery, useMutation } from '@tanstack/react-query';
import { getAuthUser } from '../../../utils/auth.util';
import { getCompanyCode } from '../../../config/axios.config';
import { fetchEventDetails } from '../../../store/apis/eventService';
import { fetchEmployeeDetails } from '../../../store/apis/employeeService';

export const EventContext = createContext();

function EventProvider({ children }) {
  const eventFontColors = useMemo(
    () => ({
      'Planned Event': '#026AA2',
      'System Event': '#131C6D',
      'Annual Leave': '#9F1AB1',
      'Other Leave': '#E04F16',
      Today: '#2C5A41',
      Weekend: '#667085',
    }),
    [],
  );

  const eventBorderColors = useMemo(
    () => ({
      'Planned Event': '#0DA6EA',
      'System Event': '#BA24D5',
      'Annual Leave': '#BA24D5',
      'Other Leave': '#E04F16',
      Today: '#47CD89',
      Weekend: '#DADCE0',
    }),
    [],
  );

  const eventBackgroundColors = useMemo(
    () => ({
      'Planned Event': '#7CD4FD',
      'System Event': '#F6D0FE',
      'Annual Leave': '#F6D0FE',
      'Other Leave': '#F9DBAF',
      Today: '#ABEFC6',
      Weekend: '#D0D5DD',
    }),
    [],
  );

  const { employeeId, userFullName } = getAuthUser();
  const isOwnUser =
    getAuthUser()?.employeeId?.toLowerCase() ===
    getAuthUser()?.userName?.toLowerCase();

  const [defaultSelectedEmployee, setDefaultSelectedEmployee] = useState({
    employeeId,
    coCd: getCompanyCode(),
    employeeName: isOwnUser ? userFullName : 'Loading…',
  });

  const [selectedEmployee, setSelectedEmployee] = useState(
    defaultSelectedEmployee,
  );

  const [eventDetailsMapped, setEventDetailsMapped] = useState([]);
  const [isSelectedEmployeeUser, setIsSelectedEmployeeUser] = useState(true);

  const fetchEmployeeDataMutation = useMutation({
    mutationFn: fetchEmployeeDetails,
    onSuccess: data => {
      const name = data?.[0]?.EmployeeName ?? 'Unknown';
      setDefaultSelectedEmployee(prev => ({
        ...prev,
        employeeName: name,
      }));
      setSelectedEmployee(prev => ({
        ...prev,
        employeeName: name,
      }));
    },
    onError: err => {
      toast.error(err?.message || 'Failed to fetch employee data.', {
        className: 'toast-error',
      });
      setDefaultSelectedEmployee(prev => ({
        ...prev,
        employeeName: 'Unknown',
      }));
      setSelectedEmployee(prev => ({
        ...prev,
        employeeName: 'Unknown',
      }));
    },
  });

  useEffect(() => {
    if (!isOwnUser) {
      fetchEmployeeDataMutation.mutate({
        type: 'PROFILE',
        employeeId: defaultSelectedEmployee.employeeId,
      });
    }
  }, []);

  const getEventFontColor = useCallback(ev => {
    if (ev.EventTyp === 'Planned Event') {
      return eventFontColors['Planned Event'];
    }
    if (ev.EventTyp === 'System Event') {
      return ev.Subject === 'Annual Leave'
        ? eventFontColors['Annual Leave']
        : eventFontColors['Other Leave'];
    }
    return '#000000';
  }, []);

  const getEventBackgroundColor = useCallback(ev => {
    if (ev.EventTyp === 'Planned Event') {
      return eventBackgroundColors['Planned Event'];
    }
    if (ev.EventTyp === 'System Event') {
      return ev.Subject === 'Annual Leave'
        ? eventBackgroundColors['Annual Leave']
        : eventBackgroundColors['Other Leave'];
    }
    return '#000000';
  }, []);
  const getEventBorderColor = useCallback(ev => {
    if (ev.EventTyp === 'Planned Event') {
      return eventBorderColors['Planned Event'];
    }
    if (ev.EventTyp === 'System Event') {
      return ev.Subject === 'Annual Leave'
        ? eventBorderColors['Annual Leave']
        : eventBorderColors['Other Leave'];
    }
    return '#000000';
  }, []);

  const parseUTCAsLocal = useCallback(isoString => {
    const d = new Date(isoString);
    return new Date(
      d.getUTCFullYear(),
      d.getUTCMonth(),
      d.getUTCDate(),
      d.getUTCHours(),
      d.getUTCMinutes(),
      d.getUTCSeconds(),
      d.getUTCMilliseconds(),
    );
  }, []);

  const { data: eventDetails, isLoading: eventDetailsIsLoading } = useQuery({
    queryKey: ['eventDetails', selectedEmployee],
    queryFn: () =>
      fetchEventDetails({
        employeeId: selectedEmployee.employeeId,
        coCd: selectedEmployee.coCd,
      }),
    enabled: !!selectedEmployee,
  });

  const mapEventDetailsUTC = useCallback(() => {
    if (!eventDetails) return;
    setEventDetailsMapped(
      eventDetails.map(e => ({
        ...e,
        start: parseUTCAsLocal(e.StartTime),
        end: parseUTCAsLocal(e.EndTime),
      })),
    );
  }, [eventDetails, parseUTCAsLocal]);

  useEffect(() => {
    if (eventDetails) mapEventDetailsUTC();
  }, [eventDetails, mapEventDetailsUTC]);

  const getDayEventDetails = useCallback(
    date => {
      const startOfDay = new Date(date);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999);
      return eventDetailsMapped.filter(({ start, end }) => {
        const s = new Date(start);
        const e = new Date(end);
        return s <= endOfDay && e >= startOfDay;
      });
    },
    [eventDetailsMapped],
  );

  const handleSelectedEmployee = useCallback(emp => {
    setSelectedEmployee(prev => ({
      ...prev,
      employeeId: emp.employeeId,
      employeeName: emp.employeeName,
    }));
  }, []);

  const handleDefaultSelectedEmployee = useCallback(() => {
    setSelectedEmployee(defaultSelectedEmployee);
  }, [defaultSelectedEmployee]);

  useEffect(() => {
    setIsSelectedEmployeeUser(selectedEmployee.employeeId === employeeId);
  }, [selectedEmployee, employeeId]);

  const providerValue = useMemo(
    () => ({
      selectedEmployee,
      setSelectedEmployee,
      eventDetailsIsLoading,
      eventDetails,
      eventDetailsMapped,
      getDayEventDetails,
      handleSelectedEmployee,
      handleDefaultSelectedEmployee,
      getEventFontColor,
      eventFontColors,
      isSelectedEmployeeUser,
      eventBorderColors,
      eventBackgroundColors,
      getEventBackgroundColor,
      getEventBorderColor,
    }),
    [
      selectedEmployee,
      eventDetailsIsLoading,
      eventDetails,
      eventDetailsMapped,
      getDayEventDetails,
      handleSelectedEmployee,
      handleDefaultSelectedEmployee,
      getEventFontColor,
      eventFontColors,
      isSelectedEmployeeUser,
      eventBorderColors,
      eventBackgroundColors,
      getEventBackgroundColor,
      getEventBorderColor,
    ],
  );

  return (
    <EventContext.Provider value={providerValue}>
      {children}
    </EventContext.Provider>
  );
}

export default EventProvider;
